import type { TaglineContent } from "../../marketing/tagline.tsx"

import * as React from "react"

import { ActionResponse, Cookie, LoaderResponse } from "@iyk/remix"
import { gtag, mixpanel, posthog } from "@iyk/tracking"
import { useLoaderData } from "@remix-run/react"
import { Data, Effect, Option, Schema } from "effect"
import { Config } from "../../Config.ts"
import { Contacts } from "../../contacts/Contacts.ts"
import { FAQ, type FAQItem } from "../../marketing/faq.tsx"
import { Footer } from "../../marketing/footer.tsx"
import { GetStarted } from "../../marketing/get-started.tsx"
import { GrabAPostcard } from "../../marketing/grab-a-postcard.tsx"
import { HeroWithTypographicReveal } from "../../marketing/hero-with-typographic-reveal.tsx"
import { LandingContactForm } from "../../marketing/landing-contact-form.tsx"
import {
  PACKAGE_SPECIFICATIONS,
  PackageDetails,
  type PackageParts,
  type PackageSlide,
} from "../../marketing/package-details.tsx"
import { PoweredByIYK } from "../../marketing/powered-by-iyk.tsx"
import { ShopNow } from "../../marketing/shop-now.tsx"
import { Testimonials } from "../../marketing/testimonials.tsx"
import { HOW_TO_TAP_LINK, IYK_MAIL, SHIPPING_AND_RETURN_LINK } from "../../ui/links.ts"
import { Remix } from "../main.server.ts"

import "../../marketing/ui/landing.css"

const ContactFormSchema = Schema.Struct({
  firstName: Schema.OptionFromNonEmptyTrimmedString.pipe(
    Schema.compose(Schema.OptionFromSelf(Contacts.FirstName)),
  ),
  lastName: Schema.OptionFromNonEmptyTrimmedString.pipe(
    Schema.compose(Schema.OptionFromSelf(Contacts.LastName)),
  ),
  quantity: Contacts.Quantity,
  email: Contacts.Email,
  message: Schema.OptionFromNonEmptyTrimmedString.pipe(
    Schema.compose(Schema.OptionFromSelf(Contacts.Message)),
  ),
}).pipe(Schema.attachPropertySignature("_tag", "Contact"))

const CartForm = Schema.Struct({
  qty: Schema.NumberFromString,
  variant: Schema.Literal("PackOfOne", "PackOfThree", "PackOfTen"),
}).pipe(Schema.attachPropertySignature("_tag", "Cart"))

export const action = Remix.action
  .formData(Schema.suspend(() => Schema.Union(ContactFormSchema, CartForm)))
  .handler(({ capabilities: { marketing, contacts }, body, args: { request } }) =>
    Effect.gen(function* () {
      const { $match } = Data.taggedEnum<typeof body>()

      return yield* $match(body, {
        Contact: (body) => {
          return contacts.submitLanding(body).pipe(
            Effect.forkDaemon,
            Effect.map(() => ActionResponse.Raw({ status: 200 })),
          )
        },
        Cart: ({ qty, variant }) => {
          const queryParams = new URL(request.url).searchParams.toString()

          const variantUrls = {
            PackOfOne: marketing.cartUrls.packOfOne,
            PackOfThree: marketing.cartUrls.packOfThree,
            PackOfTen: marketing.cartUrls.packOfTen,
          }

          return ActionResponse.TemporaryRedirect(
            `${variantUrls[variant]}:${qty}?channel=buy_button&${queryParams}`,
          )
        },
      })
    }),
  )

export const loader = Remix.loader
  .cookies(Schema.Struct({ p1Variant: Schema.optional(Schema.Literal("A", "B")) }))
  .handler(({ capabilities: { marketing, mode }, cookieJar, cookies, args: { request } }) =>
    Effect.gen(function* () {
      const searchParams = new URL(request.url).searchParams
      const p1QueryParam = searchParams.get("p1_variant")

      // If the p1_variant query param is not "A" or "B", randomly assign a variant
      const p1Variant =
        p1QueryParam === "A" || p1QueryParam === "B" ? p1QueryParam : (cookies.p1Variant ?? "A")

      // Persist the latest selected P1 variant
      if (cookies.p1Variant !== p1Variant) {
        yield* cookieJar.set(
          Cookie.make("p1Variant", p1Variant, {
            maxAge: "365 days",
            path: "/",
            httpOnly: true,
            secure: Config.Mode.is("Prod")(mode),
          }),
        )
      }

      return LoaderResponse.Ok({
        googleAnalyticsId: Option.getOrNull(marketing.googleAnalyticsId),
        metaPixelId: Option.getOrNull(marketing.metaPixelId),
        postHogId: Option.getOrNull(marketing.postHogId),
        p1Variant,
      })
    }),
  )

export default () => {
  const { p1Variant } = useLoaderData<typeof loader>()

  React.useEffect(() => {
    gtag("set", "user_properties", {
      p1_variant: p1Variant,
    })
    mixpanel.register({ p1_variant: p1Variant })
    posthog.register({ p1_variant: p1Variant })
  }, [])

  return (
    <>
      <HeroWithTypographicReveal
        heroText="A cyberphysical card that holds a personalized video message"
        taglineContent={TAGLINE_CONTENT}
        p1Variant={p1Variant}
      />
      <GetStarted />
      <GrabAPostcard />
      <Testimonials className="px-4 pb-4" />
      <PoweredByIYK className="px-4" />
      <PackageDetails.Root className="mt-[104px]">
        <PackageDetails.Parts slides={PACKAGE_SLIDES} />
        <PackageDetails.Overview parts={PACKAGE_PARTS} specifications={PACKAGE_SPECIFICATIONS} />
      </PackageDetails.Root>
      <LandingContactForm className="pt-[68px] pb-[52px]" />
      <FAQ items={FAQ_ITEMS} className="my-24" />
      <ShopNow className="my-24 px-4" />
      <Footer />
    </>
  )
}

const TAGLINE_CONTENT: TaglineContent[] = [
  { type: "word", value: "Postcard" },
  { type: "word", value: "[*]" },
  { type: "word", value: "combines" },
  { type: "word", value: "the" },
  { type: "word", value: "tactile" },
  { type: "word", value: "nostalgia" },
  { type: "word", value: "of" },
  { type: "word", value: "a" },
  { type: "word", value: "physical" },
  { type: "word", value: "card" },
  {
    type: "video",
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/typographic/typographic-1of2-physical_card.mp4",
      },
    ],
  },
  { type: "word", value: "with" },
  { type: "word", value: "the" },
  { type: "word", value: "human" },
  { type: "word", value: "connection" },
  { type: "word", value: "of" },
  { type: "word", value: "a" },
  { type: "word", value: "video" },
  { type: "word", value: "message" },
  {
    type: "video",
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/typographic/typographic-2of2-video_message.mp4",
      },
    ],
  },
]

const FAQ_ITEMS: FAQItem[] = [
  {
    id: "how-do-i-tap",
    heading: "How do I tap?",
    content: [
      `For iPhone: Hold the top edge of your iPhone near the NFC chip on the cards to access the video message.<br /><br />`,
      `For Android: Make sure NFC is enabled in your settings, then hold the center of your phone near the NFC chip to access the video message.<br /><br />`,
      `For more detailed guidance, please visit our <a href=${JSON.stringify(HOW_TO_TAP_LINK)} class="underline" rel="noreferrer" target="_blank">How to Tap guide.</a>`,
    ],
  },
  {
    id: "do-i-need-an-app-to-view-videos",
    heading: "Do I need an app to view videos?",
    content: [
      `No app is required! Just tap the Postcard [*] with a modern smartphone and open the notification to view the video message directly from your browser.`,
    ],
  },
  {
    id: "what-devices-are-compatible",
    heading: "What devices are compatible?",
    content: [
      `The Postcard [*] works with most modern smartphones equipped with NFC capabilities, including iPhone and Android devices. For details, check your device’s NFC settings.`,
    ],
  },
  {
    id: "what-are-nfc-chips",
    heading: "What are NFC chips?",
    content: [
      `NFC (Near-Field Communication) chips are small, wireless transmitters that enable devices like smartphones to communicate by being in close proximity. Our Postcards use these chips to store and transmit your video messages.`,
    ],
  },
  {
    id: "how-does-shipping-and-returns-work",
    heading: "How does shipping and returns work?",
    content: [
      `We ship domestically and internationally with standard processing times. If your order faces issues like major imperfections or software problems, reach out to us for assistance. You can find more details in our <a href=${JSON.stringify(SHIPPING_AND_RETURN_LINK)} class="underline" rel="noreferrer" target="_blank">Shipping & Returns Policy</a>.`,
    ],
  },
  {
    id: "can-you-create-custom-cyberphysical-cards",
    heading: "Can you create custom cyberphysical cards?",
    content: [
      `Yes, we offer custom cards for special occasions or branding purposes. Reach out to us through the <a class="underline" href="#custom-form">custom form</a>, and we'll get in touch to discuss your project.`,
    ],
  },
  {
    id: "is-my-video-message-secure",
    heading: "Is my video message secure?",
    content: [
      `Yes, your video messages are securely linked to the Postcard [*] using encrypted NFC technology. Each video message is accessed only through a unique, dynamic link that expires after a single tap.`,
    ],
  },
  {
    id: "help-i-cant-tap-my-card",
    heading: "Help, I can't tap my card",
    content: [
      `If your card isn't working, make sure your phone's NFC feature is enabled and that your device is in close contact with the chip. If you still face issues, try disabling the camera app or charge your phone. For more help, visit our <a href=${JSON.stringify(HOW_TO_TAP_LINK)} class="underline" rel="noreferrer" target="_blank">How to Tap guide</a> or contact us at <a class="underline" href=${`mailto:${JSON.stringify(IYK_MAIL)}`}>${IYK_MAIL}</a>.`,
    ],
  },
]

const PACKAGE_SLIDES: PackageSlide[] = [
  {
    title: "FRONT",
    image: {
      src: "https://assets.iyk-dev.app/postcards/iyk_package_1.svg",
      alt: "Front of the package",
      width: 226,
      height: 367,
    },
  },
  {
    title: "BACK",
    image: {
      src: "https://assets.iyk-dev.app/postcards/iyk_package_2.svg",
      alt: "Back of the package",
      width: 226,
      height: 367,
    },
  },
  {
    title: "ENVELOPE",
    image: {
      src: "https://assets.iyk-dev.app/postcards/iyk_package_3.svg",
      alt: "Envelope of the package",
      width: 226,
      height: 367,
    },
  },
]

const PACKAGE_PARTS: PackageParts = {
  title: "WHAT'S IN THE PACKAGE",
  items: [
    {
      description:
        "A pack of 3 or 10 cyberphysical cards with embossed silver foil graphic, printed on white 300gsm paper",
    },
    {
      description:
        "Each card contains a 25mm NFC chip - simply tap the chip to upload your video message",
    },
    {
      description: "Envelopes to package your cards",
    },
  ],
}
